import type {
  MarketRegistry,
  MarketRouteRow,
  MarketRow,
  ProviderAccountRow,
  ProviderRow,
} from '@contract';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { DataTable } from '@/components/data-table';
import { QueryState } from '@/components/feedback/query-state';
import { SkeletonTable } from '@/components/feedback/skeletons';
import { PageHeader } from '@/components/page-header';
import { StatusPill } from '@/components/primitives/status-pill';
import { adminQuery } from '@/lib/query';
import {
  capabilityLabel,
  networkLabel,
  providerName,
  routeHealth,
  routeSections,
} from '@/lib/registry';

export const Route = createFileRoute('/_app/registry')({
  component: RegistryPage,
});

/** What the market registry says: read only, editing stays on Settings. */
function RegistryPage() {
  const registry = useQuery(adminQuery('markets.registry', {}));
  return (
    <>
      <PageHeader
        title="Market registry"
        description="The markets this system serves, the providers each works with, the accounts they hold for Sao by role, and the route each channel uses now. To change a route, use Settings."
      />
      <QueryState
        query={registry}
        label="the market registry"
        skeleton={<SkeletonTable columns={4} />}
      >
        {(data) => <Registry registry={data} />}
      </QueryState>
    </>
  );
}

function Registry({ registry }: Readonly<{ registry: MarketRegistry }>) {
  return (
    <div className="flex flex-col gap-10">
      <Section
        id="markets"
        title="Markets"
        blurb={`Home market: ${registry.home}. A market that is switched off stops every money surface in it.`}
      >
        <DataTable
          id="markets"
          caption="Markets"
          columns={MARKET_COLUMNS}
          data={registry.markets}
          empty="No markets."
        />
      </Section>
      <Section
        id="providers"
        title="Providers"
        blurb="One adapter per provider per market. A paused provider carries nothing, whatever the settings say."
      >
        <DataTable
          id="providers"
          caption="Providers"
          columns={PROVIDER_COLUMNS}
          data={registry.providers}
          empty="No providers."
        />
      </Section>
      <Section
        id="accounts"
        title="Provider accounts"
        blurb="The settlement accounts each provider holds for Sao, by role and status. Account numbers and balances are not shown here."
      >
        <DataTable
          id="provider-accounts"
          caption="Provider accounts"
          columns={accountColumns(registry.providers)}
          data={registry.accounts}
          empty="No accounts."
        />
      </Section>
      {routeSections(registry.routes).map((section) => (
        <Section
          key={section.key}
          id={`routes-${section.key.replace('/', '-')}`}
          title={section.title}
          blurb="Every route the registry has for this channel. The one in use is marked; the rest say why they are not."
        >
          <DataTable
            id={`routes-${section.key.replace('/', '-')}`}
            caption={section.title}
            columns={routeColumns(registry.providers)}
            data={section.routes}
            empty="No routes."
          />
        </Section>
      ))}
    </div>
  );
}

function Section({
  id,
  title,
  blurb,
  children,
}: Readonly<{
  id: string;
  title: string;
  blurb: string;
  children: ReactNode;
}>) {
  return (
    <section aria-labelledby={`registry-${id}`} className="flex flex-col gap-3">
      <div>
        <h2 id={`registry-${id}`} className="text-base font-semibold">
          {title}
        </h2>
        <p className="text-sm text-muted-foreground">{blurb}</p>
      </div>
      {children}
    </section>
  );
}

const market = createColumnHelper<MarketRow>();
const MARKET_COLUMNS = [
  market.accessor('name', {
    header: 'Market',
    cell: ({ row }) => (
      <span>
        <span className="font-medium">{row.original.name}</span>{' '}
        <Code>{row.original.code}</Code>
      </span>
    ),
  }),
  market.accessor('home_currency', {
    header: 'Currencies',
    cell: ({ row }) => row.original.currencies.join(', '),
  }),
  market.accessor('dial_code', { header: 'Dial code' }),
  market.accessor('timezone', { header: 'Time zone' }),
  market.accessor('enabled', {
    header: 'Status',
    cell: ({ row }) => (
      <StatusPill
        tone={
          row.original.enabled && row.original.switch ? 'success' : 'danger'
        }
        label={
          !row.original.enabled
            ? 'Not enabled'
            : row.original.switch
              ? 'On'
              : 'Switched off'
        }
      />
    ),
  }),
];

const provider = createColumnHelper<ProviderRow>();
const PROVIDER_COLUMNS = [
  provider.accessor('name', {
    header: 'Provider',
    cell: ({ row }) => (
      <span>
        <span className="font-medium">{row.original.name}</span>{' '}
        <Code>{row.original.code}</Code>
      </span>
    ),
  }),
  provider.accessor('market', { header: 'Market' }),
  provider.accessor('capabilities', {
    header: 'Does',
    cell: ({ row }) =>
      row.original.capabilities.map(capabilityLabel).join(', '),
  }),
  provider.accessor('plugin', {
    header: 'Plugin',
    cell: ({ row }) => <Code>{row.original.plugin}</Code>,
  }),
  provider.accessor('status', {
    header: 'Status',
    cell: ({ row }) => <StatusPill status={row.original.status} />,
  }),
];

const account = createColumnHelper<ProviderAccountRow>();
function accountColumns(providers: readonly ProviderRow[]) {
  return [
    account.accessor('provider', {
      header: 'Provider',
      cell: ({ row }) => providerName(providers, row.original.provider),
    }),
    account.accessor('name', {
      header: 'Account',
      cell: ({ row }) => (
        <span>
          <span className="font-medium">{row.original.name}</span>{' '}
          <Code>{row.original.gl}</Code>
        </span>
      ),
    }),
    account.accessor('kind', {
      header: 'Role',
      cell: ({ row }) => row.original.kind.replaceAll('_', ' '),
    }),
    account.accessor('currency', { header: 'Currency' }),
    account.accessor('status', {
      header: 'Status',
      cell: ({ row }) => (
        <StatusPill
          status={row.original.status}
          secondary={row.original.is_default ? 'Default' : undefined}
        />
      ),
    }),
  ];
}

const route = createColumnHelper<MarketRouteRow>();
function routeColumns(providers: readonly ProviderRow[]) {
  return [
    route.accessor('network', {
      header: 'Network',
      cell: ({ row }) => networkLabel(row.original.network),
    }),
    route.accessor('code', {
      header: 'Route',
      cell: ({ row }) => <Code>{row.original.code}</Code>,
    }),
    route.accessor('provider', {
      header: 'Provider',
      cell: ({ row }) =>
        `${providerName(providers, row.original.provider)} (${row.original.rail})`,
    }),
    route.accessor('gl', {
      header: 'Account',
      cell: ({ row }) =>
        row.original.gl ? (
          <span>
            <Code>{row.original.gl}</Code> {row.original.currency}
          </span>
        ) : (
          <span className="text-muted-foreground">None</span>
        ),
    }),
    route.accessor('setting_key', {
      header: 'Chosen by',
      cell: ({ row }) =>
        row.original.setting_key ? (
          <Code>{row.original.setting_key}</Code>
        ) : (
          <span className="text-muted-foreground">No setting</span>
        ),
    }),
    route.accessor('chosen', {
      header: 'Status',
      cell: ({ row }) => {
        const why = routeHealth(row.original);
        return why ? (
          <StatusPill tone="neutral" label={why} />
        ) : (
          <StatusPill tone="success" label="In use" />
        );
      },
    }),
  ];
}

function Code({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <span className="rounded-sm bg-muted px-1 font-mono text-xs text-muted-foreground">
      {children}
    </span>
  );
}
